import { z } from 'zod';

// ===== KEYVAL & PROJECTKEYVAL =====

export const keyValGetSchema = z.object({
  key: z.string().describe("Eindeutiger Schlüssel des Eintrags. Mit `!`-Präfix wird der Wert dauerhaft gespeichert."),
  type: z.string().optional().default("custom").describe("Optionaler Typ zur Kategorisierung der Einträge."),
});

export const keyValSetSchema = z.object({
  key: z.string().describe("Eindeutiger Schlüssel des Eintrags. Mit `!`-Präfix wird der Wert dauerhaft gespeichert."),
  value: z.unknown().describe("Der zu speichernde Wert (String, Zahl, Objekt, Array etc.)."),
  type: z.string().optional().default("custom").describe("Optionaler Typ zur Kategorisierung der Einträge."),
});

// ===== MSG =====

export const msgTextSchema = z.object({
  text: z.string().describe("Der Text, der an den Bot gesendet bzw. angezeigt wird."),
});

export const msgPromptSchema = z.object({
  prompt: z.string().describe("Der neue Systemprompt für die aktuelle Sitzung."),
});

export const msgWebsearchSchema = z.object({
  enabled: z.boolean().describe("`true` aktiviert die Websuche, `false` deaktiviert sie."),
});

// ===== CARDS =====

export const cardsImageSchema = z.object({
  src: z.string().describe("Bild-URL oder Skill-ID des anzuzeigenden Bilds."),
  altText: z.string().optional().describe("Alternativtext für das Bild."),
  cssClass: z.string().optional().describe("Optionale CSS-Klasse für die Card."),
});

export const cardsIframeSchema = z.object({
  url: z.string().describe("URL der in der Card einzubettenden Seite."),
  cssClass: z.string().optional().describe("Optionale CSS-Klasse für die Card."),
});

export const cardsHtmlSchema = z.object({
  html: z.string().describe("Der anzuzeigende HTML-Inhalt."),
  cssClass: z.string().optional().describe("Optionale CSS-Klasse für die Card."),
  id: z.string().optional().describe("Optionale Element-ID für die Card."),
});

export const cardsYoutubeSchema = z.object({
  id: z.string().describe("Die YouTube-Video-ID."),
  cssClass: z.string().optional().describe("Optionale CSS-Klasse für die Card."),
});

export const cardsVimeoSchema = z.object({
  id: z.string().describe("Die Vimeo-Video-ID."),
  hash: z.string().describe("Privater Vimeo-Hash für geschützte Videos."),
  cssClass: z.string().optional().describe("Optionale CSS-Klasse für die Card."),
});

export const cardsLoadingSchema = z.object({
  text: z.string().optional().describe("Optionaler Ladetext."),
  icon: z.string().optional().describe("Optionales Icon für den Lade-Indikator."),
  cssClass: z.string().optional().describe("Optionale CSS-Klasse für die Card."),
});

// ===== OVERLAY =====

export const overlayTextSchema = z.object({
  text: z.string().describe("Der im Overlay anzuzeigende Text."),
});

export const overlayHtmlSchema = z.object({
  html: z.string().describe("Der im Overlay anzuzeigende HTML-Inhalt."),
});

export const overlayPlacementSchema = z.object({
  placement: z.enum(["botview", "fusionview"]).describe("Fenster, in dem Overlays angezeigt werden."),
});

// ===== STAGE =====

export const stageGoToNameSchema = z.object({
  name: z.string().describe("Name der Ziel-Stage."),
});

export const stageGoToIndexSchema = z.object({
  index: z.number().describe("Index der Ziel-Stage."),
});

export const stageAddSchema = z.object({
  name: z.string().describe("Eindeutiger Name der neuen Stage."),
  title: z.string().optional().describe("Anzeigetitel der Stage."),
  skills: z.array(z.string()).optional().describe("Skills, die in dieser Stage aktiv sind."),
  hidden: z.boolean().optional().describe("`true`, um die Stage aus der Stage-Liste auszublenden."),
  footer: z.boolean().optional().describe("`true`, um den Footer in dieser Stage anzuzeigen."),
  forceSkills: z.boolean().optional().describe("`true`, um die Skills dieser Stage zu erzwingen."),
  sortFloat: z.number().optional().describe("Sortierposition der Stage."),
});

export const stageGoToRandomSchema = z.object({
  possibleNames: z.array(z.string()).optional().describe("Einschränkung auf bestimmte Stage-Namen; ohne Angabe alle sichtbaren Stages."),
});

export const stageListSchema = z.object({
  includeHidden: z.boolean().optional().describe("`true`, um auch ausgeblendete Stages einzuschließen."),
});

// ===== EVENTS =====

export const eventsTriggerSchema = z.object({
  name: z.string().describe("Name des auszulösenden Events."),
  params: z.unknown().optional().describe("Parameter, die an die Event-Listener übergeben werden."),
});

export const eventsOnSchema = z.object({
  hook: z.string().describe("Name des abzuhörenden Events."),
  fn: z.unknown().describe("Die bei Event-Auslösung aufzurufende Funktion."),
  id: z.string().optional().describe("Optionale eindeutige ID des Listeners (zum späteren Entfernen)."),
});

export const eventsOffSchema = z.object({
  id: z.string().describe("ID des zu entfernenden Listeners."),
});

export const eventsInActiveHookSchema = z.object({
  seconds: z.number().describe("Anzahl der Sekunden ohne Aktivität, nach denen der Inaktivitäts-Hook ausgelöst wird."),
});

// ===== WAITFOR =====

export const waitForEventSchema = z.object({
  hook: z.string().describe("Name des abzuwartenden Events."),
});

export const waitForSecondsSchema = z.object({
  seconds: z.number().describe("Anzahl der zu wartenden Sekunden."),
  fn: z.unknown().describe("Die nach Ablauf der Wartezeit auszuführende Funktion."),
});

export const waitForMinutesSchema = z.object({
  minutes: z.number().describe("Anzahl der zu wartenden Minuten."),
  fn: z.unknown().describe("Die nach Ablauf der Wartezeit auszuführende Funktion."),
});

// ===== AUDIO =====

export const audioSaySchema = z.object({
  text: z.string().describe("Der per Text-to-Speech vorzulesende Text."),
  voice: z.string().optional().describe("Optionale Stimme, die anstelle der konfigurierten Standardstimme verwendet wird."),
});

export const audioTextSchema = z.object({
  text: z.string().describe("Der anzuzeigende bzw. zu verarbeitende Text."),
});

// ===== SCORE =====

export const scoreValueSchema = z.object({
  value: z.number().describe("Der Punktwert."),
});

export const scoreDbSchema = z.object({
  name: z.string().describe("Name des Scores."),
});

export const scoreAchieveSchema = z.object({
  value: z.number().describe("Anzahl der zu vergebenden Punkte."),
  skillId: z.string().describe("ID des Skills, der die Errungenschaft ausgelöst hat."),
  message: z.string().describe("Anzeigetext für die Errungenschaft."),
  autoMessage: z.boolean().optional().describe("`true`, um automatisch eine Standardnachricht zu generieren."),
});

// ===== SKILL =====

export const skillIdsSchema = z.object({
  skillIds: z.array(z.string()).describe("IDs der betroffenen Skills."),
});

export const skillIdDepthSchema = z.object({
  skillId: z.string().describe("ID des Wurzel-Skills."),
  depth: z.number().optional().describe("Maximale Tiefe der Kinder-Skills. Standard: unbegrenzt."),
});

// ===== DOM =====

export const domStyleVarSchema = z.object({
  key: z.string().describe("Name der CSS-Custom-Property."),
  value: z.string().describe("Der zu setzende Wert."),
});

export const domColorSchema = z.object({
  color: z.string().describe("Die zu setzende Farbe (CSS-Farbwert)."),
});

export const domUrlSchema = z.object({
  url: z.string().describe("URL der nachzuladenden Ressource."),
});

// ===== DIALOGRECORDER =====

export const dialogRecorderIdSchema = z.object({
  recorderId: z.string().describe("ID des Dialog-Recorders."),
});

export const dialogRecorderGetDialogSchema = z.object({
  recorderId: z.string().describe("ID des Dialog-Recorders."),
  userName: z.string().optional().describe("Anzeigename des Nutzers im aufgezeichneten Dialog."),
  botName: z.string().optional().describe("Anzeigename des Bots im aufgezeichneten Dialog."),
});

// ===== HELPER =====

export const helperTextSchema = z.object({
  text: z.string().describe("Der zu verarbeitende Text."),
});

export const helperStatSchema = z.object({
  stat: z.string().describe("Name der zu erfassenden Statistik."),
});

export const helperSkillIdSchema = z.object({
  skillId: z.string().describe("ID des betroffenen Skills."),
});

// ===== DOWNLOAD =====

export const downloadTextAsFileSchema = z.object({
  text: z.string().describe("Der als Datei herunterzuladende Text."),
  filename: z.string().describe("Der Dateiname für den Download."),
});

export const downloadSkillIdSchema = z.object({
  skillId: z.string().describe("ID des Skills mit dem herunterzuladenden Material."),
});

export const downloadPdfSchema = z.object({
  skillId: z.string().describe("ID des Vorlagen-Skills."),
  filename: z.string().describe("Der Dateiname für den Download."),
});

// ===== WEBHOOK =====

export const webhookCallSchema = z.object({
  skillId: z.string().describe("ID des Skills mit dem konfigurierten Webhook."),
  request: z.unknown().optional().describe("Zusätzliche Daten, die an den Webhook gesendet werden."),
});

// ===== MAIL =====

export const mailSendSchema = z.object({
  to: z.string().describe("Empfänger-Adresse."),
  subject: z.string().describe("Betreff der E-Mail."),
  body: z.string().describe("Inhalt der E-Mail."),
});

export const mailWebhookSchema = z.object({
  webhook: z.string().describe("ID des Webhooks, über den die E-Mail versendet wird."),
  envelope: z.unknown().describe("Empfänger, Betreff und Inhalt der E-Mail."),
});

// ===== CHAT_AVATAR =====

export const chatAvatarSkillIdSchema = z.object({
  skillId: z.string().describe("Bild-URL oder Skill-ID des Avatarbilds."),
});

// ===== SCORM =====

export const scormKeySchema = z.object({
  key: z.string().describe("Der SCORM-Schlüssel."),
});

export const scormSetValueSchema = z.object({
  key: z.string().describe("Der SCORM-Schlüssel."),
  value: z.string().describe("Der zu setzende Wert."),
});

// ===== AITEXT_OPENAI =====

export const aiTextOpenaiSchema = z.object({
  input: z.string().describe("Der Prompt-Text."),
  model: z.string().optional().describe("Das zu verwendende OpenAI-Modell."),
  temperature: z.number().optional().describe("Kreativität der Antwort (0 = deterministisch, höher = kreativer)."),
});

// ===== AITEXT_VLLM =====

export const aiTextInputSchema = z.object({
  input: z.string().describe("Der Prompt-Text."),
});

// ===== AIIMAGE_OPENAI / AIIMAGE_FALAI / AIIMAGE_STABLEDIFFUSION =====

export const aiImageCreateSchema = z.object({
  prompt: z.string().describe("Der Text-Prompt für die Bildgenerierung."),
  // Referenzbild als URL oder data:-URI, für Bild-Edit/img2img.
  image: z.string().optional().describe("Referenzbild-URL für die Bildbearbeitung."),
  // Mehrere Referenzbilder (URL oder data:-URI), für Bild-Edit/img2img.
  images: z.array(z.string()).optional().describe("Mehrere Referenzbilder für die Bildbearbeitung."),
  aspectRatio: z.enum(["1:1", "16:9", "9:16", "4:3", "3:4"]).optional().describe("Gewünschtes Seitenverhältnis des Ergebnisbildes."),
  quality: z.enum(["auto", "low", "medium", "high"]).optional().describe("Gewünschte Bildqualität."),
  model: z.string().optional().describe("Das zu verwendende Bildmodell."),
});

export const aiImageFileIdSchema = z.object({
  fileId: z.string().describe("ID der Bilddatei."),
});

export const aiImageAnalyzeSchema = z.object({
  image: z.string().describe("Bild-URL, das analysiert werden soll."),
  prompt: z.string().describe("Zusätzliche Anweisung für die Analyse."),
});

// ===== DOCSEARCH =====

export const docSearchSchema = z.object({
  query: z.string().describe("Der Suchbegriff."),
  skillIds: z.array(z.string()).nullable().optional().describe("Auf diese Skills beschränken, oder `null` für alle."),
  count: z.number().optional().describe("Maximale Anzahl an Treffern."),
  minScore: z.number().optional().describe("Mindest-Ähnlichkeitswert in Prozent (0-100)."),
});

// ===== FUSIONVIEW =====

export const fusionViewIdSchema = z.object({
  fusionViewId: z.string().describe("ID der Ziel-Fusion-View."),
});

// ===== SUPERVISOR =====

export const supervisorNotifySchema = z.object({
  messageName: z.string().describe("Name/Typ der Benachrichtigung."),
});

export const supervisorKeyValueSchema = z.object({
  key: z.string().describe("Eindeutiger Schlüssel der Zusatzinformation."),
  value: z.unknown().describe("Der zu speichernde Wert."),
});

export const supervisorKeySchema = z.object({
  key: z.string().describe("Der gesuchte Schlüssel."),
});

// ===== DIALOGSTORE =====

export const dialogStoreKeySchema = z.object({
  key: z.string().describe("Schlüssel/ID für die Speicherung im LocalStorage."),
});

export const dialogStoreIdSchema = z.object({
  dialogId: z.string().describe("ID des gespeicherten Dialogs."),
});

// ===== YOUTUBE =====

export const youtubeVolumeSchema = z.object({
  volume: z.number().describe("Lautstärke in Prozent (0-100)."),
});

export const youtubeRateSchema = z.object({
  rate: z.number().describe("Wiedergabegeschwindigkeit (1 = normal)."),
});

export const youtubeSeekToSchema = z.object({
  hours: z.number().optional().default(0).describe("Stunden."),
  minutes: z.number().optional().default(0).describe("Minuten."),
  seconds: z.number().optional().default(0).describe("Sekunden."),
});

export const youtubeLoadVideoSchema = z.object({
  videoId: z.string().describe("Die YouTube-Video-ID."),
});

// ===== VIMEO =====

export const vimeoSeekToSchema = z.object({
  hours: z.number().optional().default(0).describe("Stunden."),
  minutes: z.number().optional().default(0).describe("Minuten."),
  seconds: z.number().optional().default(0).describe("Sekunden."),
});

export const vimeoVolumeSchema = z.object({
  volume: z.number().describe("Lautstärke in Prozent (0-100)."),
});

export const vimeoRateSchema = z.object({
  rate: z.number().describe("Wiedergabegeschwindigkeit (1 = normal)."),
});

// ── QUIZ ───────────────────────────────────────────────────
export const quizStartFromTextSchema = z.object({
  source: z.string().describe("Ausgangstext, aus dem das Quiz generiert wird."),
});

// ── PARENT ─────────────────────────────────────────────────
export const parentCodeSchema = z.object({
  code: z.string().describe("Der im Elternfenster auszuführende Code."),
});

// ===== DEBUG =====

export const debugLogSchema = z.object({
  title: z.string().describe("Titel/Kurzbeschreibung der Debug-Meldung."),
  reason: z.string().optional().default('').describe("Zusätzlicher Beschreibungstext."),
});


// Prefixe der "wichtigen" RAGAI-Events, die im Debug-View Flow-Mode als eigene
// Kategorie geführt werden (Standard-Auswahl: nur RAGAI.FLOW.*).
export const RAGAI_DEBUG_EVENT_PREFIXES = ['RAGAI.MSG.', 'RAGAI.KEYVAL.', 'RAGAI.SKILL.', 'RAGAI.STAGE.', 'RAGAI.FLOW.'] as const;

// Prefixe, deren RAGAI-Events NICHT an den Debug-Socket weitergeleitet werden
// (Detail-Mode soll sonst möglichst ausnahmslos alle Events zeigen). Bewusst als
// Denylist: RAGAI.DEBUG.* wird bereits separat als "blockly_debug" geloggt
// (sonst Duplikat), RAGAI.EVENTS.* ist reine Listener-Registrierungs-Buchhaltung
// (kein fachliches Ereignis, sehr hohes Volumen ohne Erkenntniswert).
export const RAGAI_DEBUG_FORWARD_EXCLUDED_PREFIXES = ['RAGAI.DEBUG.', 'RAGAI.EVENTS.'] as const;
